import { Slide } from '@/components/slides/Slide.tsx';
import { FC, useEffect, useState } from 'react';
import { PeselSlide } from '@/eventConfig/types.ts';
import { RenderField } from '@/form/fields/RenderField';
import { useEventConfig } from '@/eventConfig/useEventConfig';
import { PeselCheckButton, PeselCheckStatus } from './PeselCheckButton';
import { useForm } from '@/form/useForm';
import { isPeselSkipCode, usePeselCheck } from './usePeselCheck';
import { useLayer } from '@/layers/useLayer';
import { PeselCheckResult } from './PeselCheckResult';
import { decodePeselBirthdate, isBirthdateWithinAgeRange } from './decodePeselBirthdate.ts';

interface PeselCheckSlideProps {
  className?: string;
  slide: PeselSlide;
}

export const PeselCheckSlide: FC<PeselCheckSlideProps> = ({ slide }) => {
  const spinnerDuration = 1000;
  const [peselCheckButtonDisabled, setPeselCheckButtonDisabled] = useState(true);
  const [peselCheckStatus, setPeselCheckStatus] = useState<PeselCheckStatus>('init');
  const [lastCheckedPesel, setLastCheckedPesel] = useState<string | null>(null);
  const { eventConfig } = useEventConfig();
  const { getFieldStatus, onChange } = useForm();
  const { peselCheck: doPeselCheck, peselStatus } = usePeselCheck({ spinnerDuration });
  const { showLayer } = useLayer();

  const s = eventConfig!.slides;
  const peselField = s.peselCheck.fields.find(({ name }) => name === 'pesel');
  const peselFieldStatus = getFieldStatus('pesel');
  const pesel = String(peselFieldStatus?.value?.raw ?? '');

  useEffect(() => {
    if (peselFieldStatus?.isValid) {
      setPeselCheckButtonDisabled(false);
    }
  }, [peselFieldStatus]);

  useEffect(() => {
    if (!lastCheckedPesel || pesel === lastCheckedPesel) {
      return;
    }

    // Any PESEL edit invalidates previous verification and the DOB derived from it.
    onChange('peselCheckResult', null);
    onChange('birthdate', null);
    onChange('birthdateConfirmed', null);
    setPeselCheckStatus('init');
    setLastCheckedPesel(null);
  }, [lastCheckedPesel, onChange, pesel]);

  useEffect(() => {
    const checkedPesel = lastCheckedPesel ?? pesel;

    if (checkedPesel && checkedPesel !== pesel) {
      return;
    }

    if (peselStatus === 'not-registered') {
      console.info('Pesel check ok');

      if (isPeselSkipCode(checkedPesel)) {
        onChange('birthdate', null);
        onChange('birthdateConfirmed', null);
        onChange('peselCheckResult', {
          value: { raw: 'skipped', normalized: 'skipped' },
          isValid: true,
          hint: undefined,
          slideId: slide.id,
        });
        setPeselCheckStatus('ok');
        return;
      }

      const derivedBirthdate = decodePeselBirthdate(checkedPesel);
      if (derivedBirthdate) {
        const isEligibleAge = isBirthdateWithinAgeRange(derivedBirthdate, 18, 55);

        if (!isEligibleAge) {
          onChange('peselCheckResult', {
            value: { raw: peselStatus, normalized: peselStatus },
            isValid: false,
            hint: undefined,
            slideId: slide.id,
          });
          setPeselCheckStatus('init');
          showLayer({ ref: '@ageHint' });
          return;
        }

        onChange('birthdate', {
          value: { raw: derivedBirthdate, normalized: derivedBirthdate },
          isValid: true,
          hint: undefined,
          slideId: 'birthdate',
        });
      }

      onChange('peselCheckResult', {
        value: { raw: peselStatus, normalized: peselStatus },
        isValid: true,
        hint: undefined,
        slideId: slide.id,
      });
      setPeselCheckStatus('ok');
    }

    if (peselStatus === 'already-registered-dkms') {
      console.info('Pesel is already registered (DKMS)');
      showLayer({ ref: '@alreadyRegisteredDKMS' });
      setPeselCheckStatus('not-ok');
    }

    if (peselStatus === 'already-registered-other') {
      console.info('Pesel is already registered (Other)');
      showLayer({ ref: '@alreadyRegisteredOther' });
      setPeselCheckStatus('not-ok');
    }

    if (peselStatus === 'invalid') {
      console.info('Pesel is invalid');
      onChange('pesel', {
        value: peselFieldStatus!.value,
        isValid: false,
        hint: slide.peselResultTexts?.invalid,
        slideId: slide.id,
      });
      onChange('peselCheckResult', {
        value: { raw: peselStatus, normalized: peselStatus },
        isValid: false,
        hint: undefined,
        slideId: slide.id,
      });

      setPeselCheckStatus('init');
    }

    if (peselStatus === 'poltransplant-offline') {
      console.log('Poltransplant is offline');
      onChange('peselCheckResult', {
        value: { raw: peselStatus, normalized: peselStatus },
        isValid: true,
        hint: undefined,
        slideId: slide.id,
      });
      setPeselCheckStatus('ok');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peselStatus]);

  const checkPesel = () => {
    setLastCheckedPesel(pesel);
    setPeselCheckButtonDisabled(true);
    setPeselCheckStatus('checking');
    doPeselCheck();
  };

  return (
    <Slide slide={slide}>
      <RenderField slideId={slide.id} field={peselField!} />
      <PeselCheckButton
        status={peselCheckStatus}
        disabled={peselCheckButtonDisabled}
        onCheck={checkPesel}
        slide={slide}
      />
      <PeselCheckResult peselStatus={peselStatus} slide={slide} />
    </Slide>
  );
};
